import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import bookingAPI from '../services/bookingApi';
import Navbar from '../components/Navbar';

export default function ConfirmationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialBooking = location?.state?.booking;
  const [booking, setBooking] = useState(initialBooking);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialBooking?.bookingId) {
      fetchBookingDetails(initialBooking.bookingId);
    }
  }, [initialBooking?.bookingId]);

  const fetchBookingDetails = async (bookingId) => {
    try {
      setLoading(true);
      const response = await bookingAPI.getBookingDetails(bookingId);
      if (response.success) {
        setBooking(response.data);
      } else {
        alert('Failed to load booking details');
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewBooking = () => {
    navigate('/');
  };

  const handleViewBookings = () => {
    navigate('/my-bookings');
  };

  const formatBookingDate = (booking) => {
    const bookingDateStr = booking.booking_date || booking.selectedDate || booking.date;
    if (bookingDateStr) {
      try {
        const date = new Date(bookingDateStr);
        return date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      } catch (error) {
        console.error('Error formatting booking date:', error);
        return bookingDateStr;
      }
    }
    return 'Date not specified';
  };

  const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #e0e7ff 0%, #ede9fe 50%, #dbeafe 100%)',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  };

  const contentStyle = {
    maxWidth: 720,
    margin: '0 auto',
    padding: '32px 20px 48px',
  };

  const cardStyle = {
    backgroundColor: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: 20,
    border: '1px solid rgba(255,255,255,0.9)',
    boxShadow: '0 4px 20px rgba(99,102,241,0.1)',
    padding: '32px',
    marginBottom: 20,
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <Navbar title="Booking Confirmed" />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16 }}>
          <div style={{
            width: 40, height: 40,
            border: '3px solid #e0e7ff',
            borderTop: '3px solid #6366f1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          <div style={{ color: '#6b7280', fontSize: 15 }}>Loading booking details...</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div style={pageStyle}>
        <Navbar title="Booking Confirmed" />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 20 }}>
          <div style={{ color: '#ef4444', fontSize: 18, fontWeight: 'bold' }}>Booking not found</div>
          <button
            onClick={handleNewBooking}
            style={{
              background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '12px 28px',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  const detailRows = [
    { label: 'Hall', value: booking.hall?.name },
    { label: 'Date', value: formatBookingDate(booking) },
    { label: 'Time', value: booking.timeSlot },
    { label: 'Event Title', value: booking.eventTitle },
    { label: 'Attendees', value: booking.attendees ? `${booking.attendees} people` : undefined },
    { label: 'Organization', value: booking.organization },
  ].filter(row => row.value);

  return (
    <div style={pageStyle}>
      <Navbar title="Booking Confirmed" />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={contentStyle}>

        {/* Success header card */}
        <div style={{ ...cardStyle, textAlign: 'center', padding: '40px 32px 36px' }}>
          {/* Green success circle with checkmark SVG */}
          <div style={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            backgroundColor: '#22c55e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 4px 20px rgba(34,197,94,0.3)',
          }}>
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
              <path d="M5 13l4 4L19 7" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          {/* Heading */}
          <h1 style={{
            fontSize: 26,
            fontWeight: 700,
            color: '#1e1b4b',
            margin: '0 0 10px',
            lineHeight: 1.3,
          }}>
            Booking Request Submitted!
          </h1>

          <p style={{ fontSize: 15, color: '#6b7280', margin: '0 0 24px', lineHeight: 1.6 }}>
            Your booking request has been received and is now pending approval. We will notify you via email.
          </p>

          {/* Reference ID pill */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            backgroundColor: '#eef2ff',
            border: '1px solid #c7d2fe',
            borderRadius: 999,
            padding: '8px 20px',
          }}>
            <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Booking Reference
            </span>
            <span style={{ width: 1, height: 14, backgroundColor: '#c7d2fe' }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#4338ca', letterSpacing: '0.05em' }}>
              #{booking.booking_id || booking.bookingId}
            </span>
          </div>
        </div>

        {/* Details card */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1e1b4b', margin: 0 }}>
              Booking Details
            </h2>
            {/* Status badge */}
            <span style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #fcd34d',
              color: '#92400e',
              fontSize: 12,
              fontWeight: 700,
              borderRadius: 999,
              padding: '4px 14px',
              letterSpacing: '0.03em',
            }}>
              Pending Review
            </span>
          </div>

          {detailRows.map((row, index) => (
            <div key={row.label}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
              }}>
                <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>{row.label}</span>
                <span style={{ fontSize: 14, color: '#374151', fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{row.value}</span>
              </div>
              {index < detailRows.length - 1 && (
                <div style={{ height: 1, backgroundColor: '#f3f4f6' }} />
              )}
            </div>
          ))}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={handleNewBooking}
            style={{
              flex: 1,
              minWidth: 180,
              padding: '14px 20px',
              borderRadius: 12,
              border: '1.5px solid #6366f1',
              backgroundColor: 'transparent',
              color: '#6366f1',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: "'Segoe UI', system-ui, sans-serif",
            }}
          >
            Book Another Hall
          </button>
          <button
            onClick={handleViewBookings}
            style={{
              flex: 1,
              minWidth: 180,
              padding: '14px 20px',
              borderRadius: 12,
              border: 'none',
              background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
              color: '#fff',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: "'Segoe UI', system-ui, sans-serif",
              boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
            }}
          >
            View My Bookings
          </button>
        </div>
      </div>
    </div>
  );
}
